import React, { useMemo } from 'react';
import { BaseComponentImpl } from '../../../types/implementations';
import { Building } from '../../../types/protocols';
import BlueprintWithMarkers from '../../../Components/BlueprintWithMarkers';
import { CurrentDrawing, currentDrawings } from '../../../Helpers/blueprintLookup';
import { unplacedHomes } from '../../../Helpers/componentDrawing';
import { liveRoofKeys } from '../../../Helpers/roofPlan';
import ListView from './ListView';

interface BlueprintViewProps {
    components: BaseComponentImpl[];
    building: Building;
    buildingKey: string;
    selectedKey: string | null;
    hoveredKey: string | null;
    onComponentClick: (key: string) => void;
    onComponentHover: (key: string | null) => void;
}

interface DrawingWithComponents {
    drawing: CurrentDrawing;
    components: BaseComponentImpl[];
}

const BlueprintView: React.FC<BlueprintViewProps> = ({
    components,
    building,
    buildingKey,
    selectedKey,
    hoveredKey,
    onComponentClick,
    onComponentHover,
}) => {
    const { drawingsWithComponents, unlocatedComponents } = useMemo(() => {
        const structure = building.model.structure;
        // Every current drawing: levels (ascending) and the roof drawing ("Takplan") last
        const drawings = currentDrawings(structure);
        const roofKeys = liveRoofKeys(structure);

        // A component is drawn on every current drawing it carries a pin on,
        // and listed as unlocated when a drawing it belongs on (its room's
        // level, the takplan) has no pin for it — see componentDrawing.
        const componentsByDrawing = new Map<string, BaseComponentImpl[]>();
        const unlocated: BaseComponentImpl[] = [];

        components.forEach(component => {
            drawings.forEach(drawing => {
                if (!component.blueprintLocations?.[drawing.imageKey]) return;
                const existing = componentsByDrawing.get(drawing.imageKey) ?? [];
                existing.push(component);
                componentsByDrawing.set(drawing.imageKey, existing);
            });
            if (unplacedHomes(component, building.model, roofKeys).some(home => home.kind !== 'satellite')) {
                unlocated.push(component);
            }
        });

        // Only drawings with markers, highest level first and the roof drawing last
        const result: DrawingWithComponents[] = drawings
            .filter(drawing => componentsByDrawing.has(drawing.imageKey))
            .map(drawing => ({ drawing, components: componentsByDrawing.get(drawing.imageKey)! }))
            .sort((a, b) => {
                if (a.drawing.owner.kind !== b.drawing.owner.kind) return a.drawing.owner.kind === 'roofPlan' ? 1 : -1;
                return b.drawing.order - a.drawing.order;
            });

        return { drawingsWithComponents: result, unlocatedComponents: unlocated };
    }, [components, building]);

    if (drawingsWithComponents.length === 0 && unlocatedComponents.length === 0) {
        return <p>Inga ritningar tillgängliga</p>;
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
            {drawingsWithComponents.map(({ drawing, components: drawingComponents }) => (
                <BlueprintWithMarkers
                    key={drawing.imageKey}
                    blueprint={drawing.image}
                    currentBlueprintKey={drawing.imageKey}
                    building={building}
                    utilities={drawingComponents}
                    selectedUtilityKey={selectedKey}
                    onUtilityClick={onComponentClick}
                    title={drawing.label}
                    compactMarkers
                />
            ))}

            {unlocatedComponents.length > 0 && (
                <div>
                    <h4 className="generic-component-section-header">
                        Inte på någon ritning:
                    </h4>
                    <ListView
                        components={unlocatedComponents}
                        building={building}
                        buildingKey={buildingKey}
                        selectedKey={selectedKey}
                        onComponentClick={onComponentClick}
                        groupingStrategy="none"
                    />
                </div>
            )}
        </div>
    );
};

export default BlueprintView;
